/**
 * Darstellung von Werten für die Oberfläche. Deutsche Konvention: `.` als
 * Tausender-, `,` als Dezimaltrennzeichen, Datum `DD.MM.YYYY`.
 */

const timestampFormat = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/** Ein ISO-Zeitpunkt als `DD.MM.YYYY, HH:MM:SS`. Unlesbares bleibt unverändert. */
export function formatTimestamp(iso: string): string {
  const timestamp = new Date(iso);

  return Number.isNaN(timestamp.getTime()) ? iso : timestampFormat.format(timestamp);
}

const clockFormat = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * Ein ISO-Zeitpunkt als `HH:MM` — die Spalte eines Zeitverlaufs.
 *
 * Nur die Uhrzeit, weil ein Zeitverlauf sie untereinander stellt und das Datum
 * dort fünfmal dasselbe wiederholte. Der vollständige Zeitpunkt gehört daneben
 * in den Titel; abgeschnitten ist er nicht, nur nicht ständig sichtbar.
 */
export function formatClock(iso: string): string {
  const timestamp = new Date(iso);

  return Number.isNaN(timestamp.getTime()) ? iso : clockFormat.format(timestamp);
}

/**
 * Ein reines Kalenderdatum (`2026-08-12`) als `DD.MM.YYYY`.
 *
 * **Textlich umgestellt und nicht über `Date` gerechnet**: Ein Datum ohne Uhrzeit
 * ist ein Kalendertag und kein Zeitpunkt. `new Date("2026-08-12")` wäre
 * Mitternacht UTC, und in einer Zeitzone westlich davon stünde hier der Vortag —
 * das Auslieferungsdatum einer Fassung ist aber ein Tag und keine Sekunde.
 * Unlesbares bleibt unverändert.
 */
export function formatDate(iso: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);

  return parts === null ? iso : `${parts[3]}.${parts[2]}.${parts[1]}`;
}

const dateFormat = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

/** Ab hier ist der Abstand keine Auskunft mehr, sondern nur noch eine große Zahl. */
const DISTANT_DAYS = 30;

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Ein Zeitpunkt als **Abstand zu jetzt** — „vor 5 Minuten", „vor 13 Stunden",
 * „vor 3 Tagen".
 *
 * Der Revisionsstrom wird gelesen, um zu sehen, was gerade hereingekommen ist.
 * „vor 4 Minuten" beantwortet diese Frage; `10.08.2026, 18:22:07` muss dafür
 * erst im Kopf von der aktuellen Uhrzeit abgezogen werden. Der genaue Zeitpunkt
 * ist damit nicht weg — er steht im Titel daneben (`formatTimestamp`) und ist
 * einen Mauszeiger entfernt.
 *
 * **Eine Einheit, nie zwei.** In der ersten Stunde entscheidet die Minute, ob
 * etwas „gerade" hereinkam; ab der ersten Stunde entscheidet sie nichts mehr —
 * „vor 13 Stunden und 3 Minuten" sagt niemandem etwas, was „vor 13 Stunden"
 * nicht schon sagt, und war zugleich die längste Angabe der Spalte: Sie sprengte
 * die Spaltenbreite des Stroms um siebzig Pixel und schob die Liste bei Full HD
 * in den waagerechten Rollbalken. Die Minute steht dann im Titel — dort steht
 * ohnehin der ganze Zeitpunkt.
 *
 * **Jenseits von dreißig Tagen steht das Datum.** „vor 4 Monaten" ist ungenauer
 * als `12.03.2026` und zugleich schwerer einzuordnen — ab einer gewissen Ferne
 * ist der Zeitpunkt selbst die kürzere Auskunft.
 *
 * Ein Zeitpunkt in der Zukunft — die Uhr des SVN-Servers geht vor — wird zu
 * „gerade eben" und nicht zu einer negativen Zahl: Er ist keine Auskunft über
 * die Zukunft, sondern über eine ungenaue Uhr.
 *
 * `now` steht als Parameter da, damit die Regel prüfbar ist, ohne die Uhr des
 * Rechners zu stellen.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const timestamp = new Date(iso);

  if (Number.isNaN(timestamp.getTime())) {
    return iso;
  }

  const seconds = Math.max(0, Math.round((now.getTime() - timestamp.getTime()) / 1000));

  if (seconds < 45) {
    return "gerade eben";
  }

  if (seconds < HOUR) {
    return `vor ${countOf(Math.max(1, Math.floor(seconds / MINUTE)), "Minute", "Minuten")}`;
  }

  if (seconds < DAY) {
    return `vor ${countOf(Math.floor(seconds / HOUR), "Stunde", "Stunden")}`;
  }

  const days = Math.floor(seconds / DAY);

  return days > DISTANT_DAYS
    ? `am ${dateFormat.format(timestamp)}`
    : `vor ${countOf(days, "Tag", "Tagen")}`;
}

/**
 * Derselbe Abstand als **Eintrag einer Spalte**: `jetzt`, `5 Min`, `13 Std`,
 * `8 Tage`, darüber das Datum.
 *
 * Dieselbe Regel wie in `formatRelative` — eine Einheit, nie zwei, und jenseits
 * von dreißig Tagen das Datum —, nur ohne „vor" und mit abgekürzter Einheit. Der
 * Unterschied ist keiner der Genauigkeit, sondern der Stelle: In einer Spalte
 * steht kein Satz, und „vor 13 Stunden" kostet dort die Breite, die der
 * Kommentarvorschau daneben fehlt.
 *
 * **`formatRelative` bleibt und wird nicht ersetzt.** Wo der Abstand in einem
 * Satz steht — im Zustelldialog, im `aria-label` der Zustellmarke —, liest ein
 * Vorleser den ganzen Satz. Kurz wird nur, was in einer Spalte steht; der
 * vollständige Zeitpunkt steht dort ohnehin im Titel daneben.
 *
 * `now` steht als Parameter da, damit die Regel prüfbar ist, ohne die Uhr des
 * Rechners zu stellen.
 */
export function formatAge(iso: string, now: Date = new Date()): string {
  const timestamp = new Date(iso);

  if (Number.isNaN(timestamp.getTime())) {
    return iso;
  }

  const seconds = Math.max(0, Math.round((now.getTime() - timestamp.getTime()) / 1000));

  if (seconds < 45) {
    return "jetzt";
  }

  if (seconds < HOUR) {
    return `${formatNumber(Math.max(1, Math.floor(seconds / MINUTE)))} Min`;
  }

  if (seconds < DAY) {
    return `${formatNumber(Math.floor(seconds / HOUR))} Std`;
  }

  const days = Math.floor(seconds / DAY);

  return days > DISTANT_DAYS ? dateFormat.format(timestamp) : countOf(days, "Tag", "Tage");
}

/** „1 Stunde", „2 Stunden" — die Einzahl ist kein Sonderfall, sondern die Regel. */
function countOf(value: number, one: string, many: string): string {
  return `${formatNumber(value)} ${value === 1 ? one : many}`;
}

/** Eine ganze Zahl in deutscher Schreibweise. */
export function formatNumber(value: number): string {
  return value.toLocaleString("de-DE");
}

/**
 * Ein Anteil als Prozentzahl — die Abweichungskennzahl und die Deckungszahl der
 * Kontrolle schreiben sich gleich.
 *
 * `null` bleibt ein Gedankenstrich und wird keine Null: Wo es keinen Vergleich
 * gab, steht keine Zahl, die einen behauptet.
 */
export function formatShare(value: number | null): string {
  return value === null
    ? "—"
    : `${(value * 100).toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;
}
